import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import FingerprintOutlinedIcon from "@mui/icons-material/FingerprintOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import NoMealsOutlinedIcon from "@mui/icons-material/NoMealsOutlined";
import PhoneMissedOutlinedIcon from "@mui/icons-material/PhoneMissedOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { Box, type SxProps, type Theme } from "@mui/material";
import type { TFunction } from "i18next";
import type { ComponentType, FC } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CommonDashboardBox } from "../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../components/Dashboard/CommonDashboardHeader";
import { DashboardTypography, defaultSubTextSx } from "../../components/Dashboard/DashboardTypography";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { DISCIPLINARY_VIOLATION, INK_BLACK } from "../../theme/tokens";
import { type DisciplinaryReportRowData, type DisciplinaryViolation, DisciplinaryViolationType } from "../../utils/models";
import { missingPunchHours } from "./violationLabels";

// Time-based cards trail their points with accumulated minutes; missing punch trails untracked hours.
type ViolationCardMetric = "none" | "minutes" | "hours";

type ViolationCardConfig = {
	type: DisciplinaryViolationType;
	labelKey: string;
	Icon: ComponentType<{ sx?: SxProps<Theme> }>;
	color: string;
	metric: ViolationCardMetric;
};

const VIOLATION_CARDS: readonly ViolationCardConfig[] = [
	{
		type: DisciplinaryViolationType.NCNS,
		labelKey: "disciplinary.violation.ncns",
		Icon: EventBusyOutlinedIcon,
		color: DISCIPLINARY_VIOLATION.ABSENCE_INK,
		metric: "none",
	},
	{
		type: DisciplinaryViolationType.CallIn,
		labelKey: "disciplinary.violation.call_in",
		Icon: PhoneMissedOutlinedIcon,
		color: DISCIPLINARY_VIOLATION.ABSENCE_INK,
		metric: "none",
	},
	{
		type: DisciplinaryViolationType.LateArrival,
		labelKey: "disciplinary.violation.late_arrival",
		Icon: ScheduleOutlinedIcon,
		color: DISCIPLINARY_VIOLATION.TIMING_INK,
		metric: "minutes",
	},
	{
		type: DisciplinaryViolationType.HomeEarly,
		labelKey: "disciplinary.violation.home_early",
		Icon: LogoutOutlinedIcon,
		color: DISCIPLINARY_VIOLATION.TIMING_INK,
		metric: "minutes",
	},
	{
		type: DisciplinaryViolationType.ShiftDelta,
		labelKey: "disciplinary.violation.shift_delta",
		Icon: TimerOutlinedIcon,
		color: "var(--inhouse-primary-dark)",
		metric: "minutes",
	},
	{
		type: DisciplinaryViolationType.SkippedLunch,
		labelKey: "disciplinary.violation.skipped_lunch",
		Icon: NoMealsOutlinedIcon,
		color: DISCIPLINARY_VIOLATION.LUNCH_INK,
		metric: "none",
	},
	{
		type: DisciplinaryViolationType.MissingPunch,
		labelKey: "disciplinary.violation.missing_punch",
		Icon: FingerprintOutlinedIcon,
		color: DISCIPLINARY_VIOLATION.PUNCH_ICON,
		metric: "hours",
	},
] as const;

type ViolationTotal = { count: number; points: number; minutes: number; hours: number };
type ViolationTotals = Record<DisciplinaryViolationType, ViolationTotal>;

const emptyTotal = (): ViolationTotal => ({ count: 0, points: 0, minutes: 0, hours: 0 });

const emptyTotals = (): ViolationTotals => ({
	[DisciplinaryViolationType.NCNS]: emptyTotal(),
	[DisciplinaryViolationType.CallIn]: emptyTotal(),
	[DisciplinaryViolationType.LateArrival]: emptyTotal(),
	[DisciplinaryViolationType.HomeEarly]: emptyTotal(),
	[DisciplinaryViolationType.ShiftDelta]: emptyTotal(),
	[DisciplinaryViolationType.SkippedLunch]: emptyTotal(),
	[DisciplinaryViolationType.MissingPunch]: emptyTotal(),
});

// Mirrors _minutes_for in backend/services/disciplinary_overview_export.py so totals match the export.
const minutesForViolation = (v: DisciplinaryViolation): number => {
	if (!v.actualStart || !v.actualEnd) return 0;
	if (v.violationType === DisciplinaryViolationType.LateArrival) {
		return Math.max(0, v.actualStart.diff(v.plannedStart, "minutes").minutes);
	}
	if (v.violationType === DisciplinaryViolationType.HomeEarly) {
		return Math.max(0, v.plannedEnd.diff(v.actualEnd, "minutes").minutes);
	}
	if (v.violationType === DisciplinaryViolationType.ShiftDelta) {
		const planned = v.plannedEnd.diff(v.plannedStart, "minutes").minutes;
		const actual = v.actualEnd.diff(v.actualStart, "minutes").minutes;
		return Math.abs(actual - planned);
	}
	return 0;
};

const cardHeight = { xs: "92px", xl: "104px" } as const satisfies SxProps<Theme>;

const cardSx = {
	flex: 1,
	minWidth: 0,
	height: cardHeight,
	display: "flex",
	flexDirection: "column",
	alignItems: "start",
	gap: 0.5,
} as const satisfies SxProps<Theme>;

// Seven cards share one row, and the busiest subtext carries two figures ("1838 pts · 472,010 min").
// At the default size that overruns a card this narrow, so these cards step down to the small-text
// scale. The clip is a backstop for a viewport narrow enough that even this doesn't fit — the figures
// stay on one line and every card keeps its size, and the tooltip below keeps the full text reachable.
const subTextSx = {
	...defaultSubTextSx,
	fontSize: { xxl: "13px", xl: "11px", xs: "10px" },
	overflow: "hidden",
	whiteSpace: "nowrap",
	textOverflow: "ellipsis",
} satisfies SxProps<Theme>;

const formatPoints = (points: number): string => {
	const rounded = Math.round(points * 10) / 10;
	return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(1);
};

const formatMetric = (metric: ViolationCardMetric, total: ViolationTotal, t: TFunction): string => {
	if (metric === "minutes") return ` · ${Math.round(total.minutes).toLocaleString()} ${t("disciplinary.summary.minutes")}`;
	if (metric === "hours") return ` · ${formatPoints(total.hours)} ${t("disciplinary.summary.hours")}`;
	return "";
};

type Props = {
	rowData: DisciplinaryReportRowData[];
};

export const DisciplinarySummaryCards: FC<Props> = ({ rowData }) => {
	const { t } = useTranslation();
	// Only the new hospital demo (Hospital type + inhouse2) shows the icons in dark ink.
	const { isHospitalView } = useDepartmentFlags();

	const totals = useMemo<ViolationTotals>(() => {
		const acc = emptyTotals();
		for (const row of rowData) {
			for (const violation of row.violations) {
				const bucket = acc[violation.violationType];
				if (!bucket) continue;
				bucket.count += 1;
				bucket.points += violation.points;
				bucket.minutes += minutesForViolation(violation);
				bucket.hours += missingPunchHours(violation);
			}
		}
		return acc;
	}, [rowData]);

	return (
		<Box sx={{ display: "flex", gap: 1, width: "100%", flexWrap: "wrap" }}>
			{VIOLATION_CARDS.map(({ type, labelKey, Icon, color, metric }) => {
				const total = totals[type];
				const subText = `${formatPoints(total.points)} ${t("disciplinary.summary.points")}${formatMetric(metric, total, t)}`;
				return (
					<CommonDashboardBox key={type} sx={cardSx}>
						<CommonDashboardHeader leading={<Icon sx={{ color: isHospitalView ? INK_BLACK : color, fontSize: { xs: 18, xl: 22 } }} />} label={t(labelKey)} />
						<DashboardTypography mainText={total.count.toString()} subText={subText} subTextSx={subTextSx} subTextTooltip={subText} />
					</CommonDashboardBox>
				);
			})}
		</Box>
	);
};
